import "@/lib/immer-config"; // Ensure MapSet is enabled
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { useMemo } from "react";
import { z } from "zod";
import { VersioningPreferencesSchema } from "@/schemas/storage";
import { ProjectVersionSchema } from "@/schemas/versioning";
import {
  ProjectVersion,
  VersionChange,
  ProjectSnapshot,
  PageSnapshot,
  Component,
  Page,
  NavigationStyle,
  PageTemplate,
  VersionConflict,
  VersionBranch,
  VersionDraft,
  VersionComparison,
  VersioningMetadata,
} from "@/types";
import { config } from "@/config";
import { generateId } from "@/utils";
import { detectVersionConflicts, ConflictContext } from "./conflict-detection";
import { benchmarkVersioningOperation } from "./performance-benchmarks";
import { useAuthStore } from "@/stores/auth/auth-store";
import { logger } from "@/utils/logger";
import { getVersioningEditorState } from "../editor/versioning-editor-source";
import { getCurrentAuthHeaders } from "@/services/api/auth-session";

// Re-export types for convenience
export type {
  ProjectVersion,
  VersionBranch,
  VersionDraft,
  VersionComparison,
  VersionConflict,
  VersioningMetadata,
  ProjectSnapshot,
  VersionChange,
} from "@/types";

// Main versioning store state
export interface VersioningState {
  // Core version data
  projectId: string | null;
  versions: ProjectVersion[];
  versionMap: Map<number, ProjectVersion>;
  currentVersion: number | null;
  selectedVersion: number | null;

  // Branch management
  branches: VersionBranch[];
  branchMap: Map<string, VersionBranch>;
  currentBranch: string | null;

  // Draft management
  drafts: VersionDraft[];
  draftMap: Map<string, VersionDraft>;
  currentDraft: string | null;

  // Comparison and conflicts
  comparisons: VersionComparison[];
  comparisonMap: Map<string, VersionComparison>;
  currentComparison: string | null;
  activeConflicts: VersionConflict[];
  resolvedConflicts: VersionConflict[];

  // UI state
  isLoading: boolean;
  error: string | null;
  lastUpdateTimestamp: number;

  // View settings
  showAllVersions: boolean;
  showBranchVersionsOnly: boolean;
  versionFilterTag?: string;
  sortBy: "date" | "version" | "author";
  sortOrder: "asc" | "desc";

  // Performance optimization
  enableVersionCaching: boolean;
  versionCache: Map<string, unknown>;
  lastSyncTimestamp: number;

  // Change tracking
  pendingChanges: VersionChange[];
  changeBuffer: VersionChange[]; // Alias for pendingChanges (test compatibility)

  // Local branching (session-scoped, no backend persistence)
  localBranches: Map<string, { name: string; description?: string; snapshot: ProjectSnapshot; createdAt: Date; sourceVersionId?: number }>;
  localBranchBeforeSnapshot: ProjectSnapshot | null;

  // Metadata
  metadata: VersioningMetadata;
}

export interface VersioningActions {
  // Project initialization
  initializeVersioning: (projectId: string) => Promise<void>;
  loadVersionHistory: (projectId: string) => Promise<void>;
  refreshVersionData: () => Promise<void>;

  // Version management
  createVersion: (data: {
    name?: string;
    description?: string;
    snapshot: ProjectSnapshot;
    changes: VersionChange[];
    tags?: string[];
  }) => Promise<ProjectVersion>;
  deleteVersion: (versionNumber: number) => Promise<void>;
  restoreVersion: (versionNumber: number) => Promise<void>;
  publishVersion: (versionNumber: number) => Promise<void>;
  setCurrentVersion: (versionNumber: number) => void;
  selectVersion: (versionNumber: number | null) => void;

  // Branch management
  createBranch: (data: {
    name: string;
    description?: string;
    baseVersion: number;
  }) => Promise<VersionBranch>;
  deleteBranch: (branchId: string) => Promise<void>;
  switchToBranch: (branchId: string) => void;
  mergeBranch: (branchId: string, targetBranch?: string) => Promise<void>;

  // Draft management
  createDraft: (data: {
    name: string;
    description?: string;
    branchId?: string;
    snapshot: ProjectSnapshot;
    changes: VersionChange[];
  }) => Promise<VersionDraft>;
  updateDraft: (
    draftId: string,
    updates: Partial<VersionDraft>,
  ) => Promise<void>;
  deleteDraft: (draftId: string) => Promise<void>;
  submitDraftForReview: (draftId: string) => Promise<void>;
  approveDraft: (draftId: string) => Promise<void>;
  rejectDraft: (draftId: string, reason?: string) => Promise<void>;
  publishDraft: (draftId: string) => Promise<ProjectVersion>;

  // Comparison and conflict resolution
  compareVersions: (
    fromVersion: number,
    toVersion: number,
  ) => Promise<VersionComparison>;
  compareBranches: (
    fromBranch: string,
    toBranch: string,
  ) => Promise<VersionComparison>;
  resolveConflict: (
    conflictId: string,
    resolution: {
      type: "use_source" | "use_target" | "merge" | "custom";
      customValue?: unknown;
    },
  ) => Promise<void>;
  resolveAllConflicts: (
    resolutions: Array<{
      conflictId: string;
      type: "use_source" | "use_target" | "merge" | "custom";
      customValue?: unknown;
    }>,
  ) => Promise<void>;

  // Utility actions
  getVersion: (versionNumber: number) => ProjectVersion | undefined;
  getBranch: (branchId: string) => VersionBranch | undefined;
  getDraft: (draftId: string) => VersionDraft | undefined;
  getComparison: (comparisonId: string) => VersionComparison | undefined;

  // Version history navigation
  getVersionChanges: (versionNumber: number) => VersionChange[];
  getVersionsInRange: (
    startVersion: number,
    endVersion: number,
  ) => ProjectVersion[];
  getVersionsByAuthor: (authorId: string) => ProjectVersion[];
  getVersionsByTag: (tag: string) => ProjectVersion[];
  getVersionsByDateRange: (startDate: Date, endDate: Date) => ProjectVersion[];

  // Snapshot utilities
  generateSnapshot: (projectId: string) => Promise<ProjectSnapshot>;
  validateSnapshot: (snapshot: ProjectSnapshot) => Promise<boolean>;
  applySnapshot: (snapshot: ProjectSnapshot) => Promise<void>;

  // UI and view management
  setVersionFilter: (filter: {
    showAllVersions?: boolean;
    showBranchVersionsOnly?: boolean;
    tag?: string;
  }) => void;
  setSortOptions: (
    sortBy: "date" | "version" | "author",
    sortOrder: "asc" | "desc",
  ) => void;
  clearSelection: () => void;

  // Error handling
  setError: (error: string | null) => void;
  clearError: () => void;

  // Performance and caching
  enableCaching: (enabled: boolean) => void;
  clearCache: () => void;
  preloadVersions: (versionNumbers: number[]) => Promise<void>;

  // Change tracking methods
  trackComponentChange: (
    changeType: "create" | "update" | "delete",
    componentId: string,
    pageId: string,
    field?: string,
    oldValue?: unknown,
    newValue?: unknown,
    description?: string,
  ) => void;
  trackPageChange: (
    changeType: "create" | "update" | "delete",
    pageId: string,
    field?: string,
    oldValue?: unknown,
    newValue?: unknown,
    description?: string,
  ) => void;
  trackChange: (change: VersionChange) => void;
  clearChanges: () => void;
  getPendingChanges: () => VersionChange[];
  addChangeToTracking: (change: VersionChange) => void;

  // Snapshot utilities (enhanced)
  createSnapshot: (
    pages: Page[],
    pageComponents: Map<string, Component[]>,
  ) => ProjectSnapshot;

  // Enhanced version comparison with conflict detection
  compareVersionsWithConflictDetection: (
    baseSnapshot: ProjectSnapshot,
    sourceSnapshot: ProjectSnapshot,
    targetSnapshot: ProjectSnapshot,
  ) => Promise<VersionComparison>;
}

export interface VersioningStore extends VersioningState, VersioningActions {}

// Export config type for external use
export interface VersioningConfig {
  enableVersionCaching: boolean;
  maxVersions: number;
  autoSaveInterval: number;
  requireApproval: boolean;
}

const VersionDraftSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  baseVersion: z.number(),
  branchId: z.string().optional(),
  changes: ProjectVersionSchema.shape.changes,
  snapshot: ProjectVersionSchema.shape.snapshot,
  createdBy: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isApproved: z.boolean().optional(),
  approvedBy: z.string().optional(),
  approvedAt: z.coerce.date().optional(),
  status: z.enum(["draft", "pending_review", "approved", "rejected"]),
});

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function getResponseData(value: unknown): Record<string, unknown> | null {
  if (!isRecord(value) || !isRecord(value.data)) {
    return null;
  }

  return value.data;
}

const getVersioningRequestHeaders = (): Record<string, string> => ({
  ...getCurrentAuthHeaders(),
  "Content-Type": "application/json",
});

export const useVersioningStore = create<VersioningStore>()(
  subscribeWithSelector(
    immer((set, get) => ({
      // Initial state
      projectId: null,
      versions: [],
      versionMap: new Map(),
      currentVersion: null,
      selectedVersion: null,

      branches: [],
      branchMap: new Map(),
      currentBranch: null,

      drafts: [],
      draftMap: new Map(),
      currentDraft: null,

      comparisons: [],
      comparisonMap: new Map(),
      currentComparison: null,
      activeConflicts: [],
      resolvedConflicts: [],

      isLoading: false,
      error: null,
      lastUpdateTimestamp: Date.now(),

      showAllVersions: true,
      showBranchVersionsOnly: false,
      sortBy: "date",
      sortOrder: "desc",

      enableVersionCaching: true,
      versionCache: new Map(),
      lastSyncTimestamp: 0,

      // Change tracking initialization
      pendingChanges: [],
      changeBuffer: [], // Will be kept in sync with pendingChanges

      localBranches: new Map(),
      localBranchBeforeSnapshot: null,

      metadata: {
        totalVersions: 0,
        totalBranches: 0,
        totalDrafts: 0,
        hasUnresolvedConflicts: false,
        pendingDrafts: 0,
      },

      // Actions implementation
      initializeVersioning: async (projectId: string) => {
        set((state) => {
          state.projectId = projectId;
          state.isLoading = true;
          state.error = null;
        });

        try {
          await get().loadVersionHistory(projectId);
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to initialize versioning";
            state.isLoading = false;
          });
        }
      },

      loadVersionHistory: async (projectId: string) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          // Load versions from API
          const versionsResponse = await fetch(
            `${config.api.baseUrl}/versioning/projects/${projectId}/versions`,
            {
              headers: getVersioningRequestHeaders(),
            },
          );

          if (!versionsResponse.ok) {
            throw new Error(
              `Failed to load versions: ${versionsResponse.statusText}`,
            );
          }

          const versionsPayload: unknown = await versionsResponse.json();
          const versionsData = getResponseData(versionsPayload);
          const versions = z.array(ProjectVersionSchema).parse(
            versionsData?.versions ?? [],
          );

          // Load branches from API when implemented
          // For now, no default branches - will show empty state
          const branches: VersionBranch[] = [];

          // Load drafts
          const draftsUrl = `${config.api.baseUrl}/versioning/projects/${projectId}/drafts`;

          const draftsResponse = await fetch(draftsUrl, {
            headers: getVersioningRequestHeaders(),
          });

          const draftsPayload: unknown = draftsResponse.ok
            ? await draftsResponse.json()
            : { data: { drafts: [] } };
          const draftsData = getResponseData(draftsPayload);
          const drafts = z.array(VersionDraftSchema).parse(
            draftsData?.drafts ?? [],
          );

          set((state) => {
            // Update versions
            state.versions = versions;
            state.versionMap = new Map(versions.map((v: ProjectVersion) => [v.version, v]));

            // Update branches
            state.branches = branches;
            state.branchMap = new Map(branches.map((b: VersionBranch) => [b.id, b]));

            // Update drafts
            state.drafts = drafts;
            state.draftMap = new Map(drafts.map((d: VersionDraft) => [d.id, d]));

            // Update metadata
            state.metadata = {
              totalVersions: versions.length,
              totalBranches: branches.length,
              totalDrafts: drafts.length,
              hasUnresolvedConflicts: state.activeConflicts.length > 0,
              pendingDrafts: drafts.filter((d: VersionDraft) => d.status === "pending_review")
                .length,
              lastVersionCreated:
                versions.length > 0
                  ? versions[versions.length - 1]?.createdAt
                  : undefined,
              lastVersionCreatedBy:
                versions.length > 0
                  ? versions[versions.length - 1]?.createdBy
                  : undefined,
            };

            state.isLoading = false;
            state.lastUpdateTimestamp = Date.now();
            state.lastSyncTimestamp = Date.now();
          });
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to load version history";
            state.isLoading = false;
          });
        }
      },

      refreshVersionData: async () => {
        const { projectId } = get();
        if (projectId) {
          await get().loadVersionHistory(projectId);
        }
      },

      createVersion: async (data) => {
        const { projectId } = get();
        if (!projectId) throw new Error("No project loaded");

        try {
          const response = await fetch(
            `${config.api.baseUrl}/versioning/projects/${projectId}/versions`,
            {
              method: "POST",
              headers: getVersioningRequestHeaders(),
              body: JSON.stringify(data),
            },
          );

          if (!response.ok) {
            throw new Error(`Failed to create version: ${response.statusText}`);
          }

          const result = await response.json();
          const newVersion = result.data;

          set((state) => {
            state.versions.push(newVersion);
            state.versionMap.set(newVersion.version, newVersion);
            state.currentVersion = newVersion.version;
            state.metadata.totalVersions += 1;
            state.metadata.lastVersionCreated = newVersion.createdAt;
            state.metadata.lastVersionCreatedBy = newVersion.createdBy;
            state.lastUpdateTimestamp = Date.now();
          });

          return newVersion;
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to create version";
          });
          throw error;
        }
      },

      deleteVersion: async (versionNumber: number) => {
        const { projectId } = get();
        if (!projectId) throw new Error("No project loaded");

        try {
          const response = await fetch(
            `${config.api.baseUrl}/versioning/projects/${projectId}/versions/${versionNumber}`,
            {
              method: "DELETE",
              headers: getVersioningRequestHeaders(),
            },
          );

          if (!response.ok) {
            throw new Error(`Failed to delete version: ${response.statusText}`);
          }

          set((state) => {
            state.versions = state.versions.filter(
              (v) => v.version !== versionNumber,
            );
            state.versionMap.delete(versionNumber);

            if (state.currentVersion === versionNumber) {
              state.currentVersion =
                state.versions.length > 0
                  ? Math.max(...state.versions.map((v) => v.version))
                  : null;
            }
            if (state.selectedVersion === versionNumber) {
              state.selectedVersion = null;
            }

            state.metadata.totalVersions -= 1;
            state.lastUpdateTimestamp = Date.now();
          });
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to delete version";
          });
          throw error;
        }
      },

      restoreVersion: async (versionNumber: number) => {
        const { projectId } = get();
        if (!projectId) throw new Error("No project loaded");

        try {
          const response = await fetch(
            `${config.api.baseUrl}/projects/${projectId}/versions/${versionNumber}/restore`,
            {
              method: "POST",
              headers: getVersioningRequestHeaders(),
            },
          );

          if (!response.ok) {
            throw new Error(
              `Failed to restore version: ${response.statusText}`,
            );
          }

          const result = await response.json();

          set((state) => {
            state.currentVersion = versionNumber;
            state.lastUpdateTimestamp = Date.now();
          });

          return result.data;
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to restore version";
          });
          throw error;
        }
      },

      publishVersion: async (versionNumber: number) => {
        const { projectId } = get();
        if (!projectId) throw new Error("No project loaded");

        try {
          const response = await fetch(
            `${config.api.baseUrl}/projects/${projectId}/versions/${versionNumber}/publish`,
            {
              method: "POST",
              headers: getVersioningRequestHeaders(),
            },
          );

          if (!response.ok) {
            throw new Error(
              `Failed to publish version: ${response.statusText}`,
            );
          }

          set((state) => {
            const version = state.versionMap.get(versionNumber);
            if (version) {
              version.isPublished = true;
              state.lastUpdateTimestamp = Date.now();
            }
          });
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to publish version";
          });
          throw error;
        }
      },

      setCurrentVersion: (versionNumber: number) => {
        set((state) => {
          state.currentVersion = versionNumber;
          state.lastUpdateTimestamp = Date.now();
        });
      },

      selectVersion: (versionNumber: number | null) => {
        set((state) => {
          state.selectedVersion = versionNumber;
        });
      },

      // Branch management actions (local snapshot-based branching — session-scoped)
      createBranch: async (data) => {
        const { currentVersion } = get();

        // Capture current editor state as snapshot for this branch
        const editorState = getVersioningEditorState();
        if (!editorState) {
          throw new Error("Editor state not registered for versioning");
        }
        const pages = editorState.pages || [];
        const pageComponents = new Map(editorState.pageComponents);
        const snapshot = get().createSnapshot(pages, pageComponents);

        // Save the pre-branch snapshot so we can restore on merge/switch back
        set((state) => {
          if (!state.localBranchBeforeSnapshot) {
            state.localBranchBeforeSnapshot = snapshot;
          }
          state.localBranches.set(data.name, {
            name: data.name,
            description: data.description,
            snapshot,
            createdAt: new Date(),
            sourceVersionId: currentVersion || undefined,
          });
        });

        logger.info(`[Phase6:Versioning] createBranch`, { branchName: data.name, baseVersion: currentVersion });

        const branch: VersionBranch = {
          id: data.name,
          name: data.name,
          description: data.description,
          baseVersion: data.baseVersion || currentVersion || 0,
          headVersion: data.baseVersion || currentVersion || 0,
          createdBy: 'local',
          createdAt: new Date(),
          updatedAt: new Date(),
          isProtected: false,
          status: 'active',
        };

        set((state) => {
          state.branches.push(branch);
          state.branchMap.set(branch.id, branch);
          state.currentBranch = branch.id;
          state.lastUpdateTimestamp = Date.now();
        });

        return branch;
      },

      deleteBranch: async (branchId: string) => {
        logger.info(`[Phase6:Versioning] deleteBranch`, { branchId });
        set((state) => {
          state.localBranches.delete(branchId);
          state.branches = state.branches.filter(b => b.id !== branchId);
          state.branchMap.delete(branchId);
          if (state.currentBranch === branchId) {
            state.currentBranch = null;
          }
          state.lastUpdateTimestamp = Date.now();
        });
      },

      switchToBranch: (branchId: string) => {
        logger.info(`[Phase6:Versioning] switchToBranch`, { branchId });
        const localBranch = get().localBranches.get(branchId);
        set((state) => {
          state.currentBranch = branchId;
          state.lastUpdateTimestamp = Date.now();
        });
        // If there's a local snapshot, apply it
        if (localBranch?.snapshot) {
          get().applySnapshot(localBranch.snapshot).catch(() => {});
        }
      },

      mergeBranch: async (branchId: string, _targetBranch?: string) => {
        logger.info(`[Phase6:Versioning] mergeBranch start`, { branchId });
        const localBranch = get().localBranches.get(branchId);
        if (!localBranch) {
          throw new Error(`Branch "${branchId}" not found`);
        }
        // Replace main state with branch state
        await get().applySnapshot(localBranch.snapshot);
        // Remove the branch
        set((state) => {
          state.localBranches.delete(branchId);
          state.branches = state.branches.filter(b => b.id !== branchId);
          state.branchMap.delete(branchId);
          if (state.currentBranch === branchId) {
            state.currentBranch = null;
          }
          state.localBranchBeforeSnapshot = null;
          state.lastUpdateTimestamp = Date.now();
        });
        logger.info(`[Phase6:Versioning] mergeBranch complete`, { branchId });
      },

      // Draft management actions
      createDraft: async (data) => {
        const { projectId } = get();
        if (!projectId) throw new Error("No project loaded");

        try {
          const url = `${config.api.baseUrl}/versioning/projects/${projectId}/drafts`;

          const response = await fetch(url, {
            method: "POST",
            headers: getVersioningRequestHeaders(),
            body: JSON.stringify(data),
          });

          if (!response.ok) {
            throw new Error(`Failed to create draft: ${response.statusText}`);
          }

          const resultPayload: unknown = await response.json();
          const resultData = getResponseData(resultPayload);
          const newDraft = VersionDraftSchema.parse(resultData);

          logger.debug("[VersioningStore] Draft created", {
            projectId,
            draftId: newDraft.id,
            status: newDraft.status,
          });

          set((state) => {
            state.drafts.push(newDraft);
            state.draftMap.set(newDraft.id, newDraft);
            state.currentDraft = newDraft.id;
            state.metadata.totalDrafts += 1;
            if (newDraft.status === "pending_review") {
              state.metadata.pendingDrafts += 1;
            }
            state.lastUpdateTimestamp = Date.now();
          });

          return newDraft;
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error ? error.message : "Failed to create draft";
          });
          throw error;
        }
      },

      updateDraft: async (draftId: string, updates: Partial<VersionDraft>) => {
        const { projectId } = get();
        if (!projectId) throw new Error("No project loaded");

        try {
          const response = await fetch(
            `${config.api.baseUrl}/versioning/projects/${projectId}/drafts/${draftId}`,
            {
              method: "PATCH",
              headers: getVersioningRequestHeaders(),
              body: JSON.stringify(updates),
            },
          );

          if (!response.ok) {
            throw new Error(`Failed to update draft: ${response.statusText}`);
          }

          const result = await response.json();
          const updatedDraft = result.data;

          set((state) => {
            const existingDraft = state.draftMap.get(draftId);
            if (
              existingDraft &&
              existingDraft.status === "pending_review" &&
              updatedDraft.status !== "pending_review"
            ) {
              state.metadata.pendingDrafts -= 1;
            } else if (
              !existingDraft ||
              (existingDraft.status !== "pending_review" &&
                updatedDraft.status === "pending_review")
            ) {
              state.metadata.pendingDrafts += 1;
            }

            state.draftMap.set(draftId, updatedDraft);
            const index = state.drafts.findIndex((d) => d.id === draftId);
            if (index !== -1) {
              state.drafts[index] = updatedDraft;
            }
            state.lastUpdateTimestamp = Date.now();
          });
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error ? error.message : "Failed to update draft";
          });
          throw error;
        }
      },

      deleteDraft: async (draftId: string) => {
        const { projectId } = get();
        if (!projectId) throw new Error("No project loaded");

        try {
          const response = await fetch(
            `${config.api.baseUrl}/versioning/projects/${projectId}/drafts/${draftId}`,
            {
              method: "DELETE",
              headers: getVersioningRequestHeaders(),
            },
          );

          if (!response.ok) {
            throw new Error(`Failed to delete draft: ${response.statusText}`);
          }

          set((state) => {
            const draft = state.draftMap.get(draftId);
            if (draft && draft.status === "pending_review") {
              state.metadata.pendingDrafts -= 1;
            }

            state.drafts = state.drafts.filter((d) => d.id !== draftId);
            state.draftMap.delete(draftId);

            if (state.currentDraft === draftId) {
              state.currentDraft = null;
            }

            state.metadata.totalDrafts -= 1;
            state.lastUpdateTimestamp = Date.now();
          });
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error ? error.message : "Failed to delete draft";
          });
          throw error;
        }
      },

      submitDraftForReview: async (draftId: string) => {
        await get().updateDraft(draftId, { status: "pending_review" });
      },

      approveDraft: async (draftId: string) => {
        await get().updateDraft(draftId, {
          status: "approved",
          isApproved: true,
          approvedAt: new Date(),
        });
      },

      rejectDraft: async (draftId: string, reason?: string) => {
        await get().updateDraft(draftId, {
          status: "rejected",
          description: reason
            ? `${get().draftMap.get(draftId)?.description || ""}\n\nRejection reason: ${reason}`
            : undefined,
        });
      },

      publishDraft: async (draftId: string) => {
        const draft = get().draftMap.get(draftId);
        if (!draft) throw new Error("Draft not found");

        const newVersion = await get().createVersion({
          name: draft.name,
          description: draft.description,
          snapshot: draft.snapshot,
          changes: draft.changes,
        });

        // Delete the draft after successful publication
        await get().deleteDraft(draftId);

        return newVersion;
      },

      // Comparison and conflict resolution
      compareVersions: async (fromVersion: number, toVersion: number) => {
        const { projectId } = get();
        if (!projectId) throw new Error("No project loaded");

        try {
          const response = await fetch(
            `${config.api.baseUrl}/projects/${projectId}/versions/compare`,
            {
              method: "POST",
              headers: getVersioningRequestHeaders(),
              body: JSON.stringify({ fromVersion, toVersion }),
            },
          );

          if (!response.ok) {
            throw new Error(
              `Failed to compare versions: ${response.statusText}`,
            );
          }

          const result = await response.json();
          const comparison = result.data;

          set((state) => {
            state.comparisons.push(comparison);
            state.comparisonMap.set(comparison.id, comparison);
            state.currentComparison = comparison.id;

            if (comparison.conflictsDetected) {
              state.activeConflicts.push(...comparison.conflicts);
              state.metadata.hasUnresolvedConflicts = true;
            }

            state.lastUpdateTimestamp = Date.now();
          });

          return comparison;
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to compare versions";
          });
          throw error;
        }
      },

      compareBranches: async (_fromBranch: string, _toBranch: string) => {
        // Branch functionality not yet implemented in backend
        throw new Error(
          "Branch comparison is not yet implemented. Please use version comparison for now.",
        );
      },

      resolveConflict: async (conflictId: string, resolution) => {
        const { projectId } = get();
        if (!projectId) throw new Error("No project loaded");

        try {
          const response = await fetch(
            `${config.api.baseUrl}/projects/${projectId}/versions/conflicts/${conflictId}/resolve`,
            {
              method: "POST",
              headers: getVersioningRequestHeaders(),
              body: JSON.stringify(resolution),
            },
          );

          if (!response.ok) {
            throw new Error(
              `Failed to resolve conflict: ${response.statusText}`,
            );
          }

          set((state) => {
            const conflictIndex = state.activeConflicts.findIndex(
              (c) => c.id === conflictId,
            );
            if (conflictIndex !== -1) {
              const conflict = state.activeConflicts[conflictIndex];
              conflict.resolution = resolution.type;
              conflict.customValue = resolution.customValue;
              conflict.resolvedAt = new Date();

              state.resolvedConflicts.push(conflict);
              state.activeConflicts.splice(conflictIndex, 1);

              if (state.activeConflicts.length === 0) {
                state.metadata.hasUnresolvedConflicts = false;
              }
            }
            state.lastUpdateTimestamp = Date.now();
          });
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to resolve conflict";
          });
          throw error;
        }
      },

      resolveAllConflicts: async (resolutions) => {
        for (const resolution of resolutions) {
          await get().resolveConflict(resolution.conflictId, {
            type: resolution.type,
            customValue: resolution.customValue,
          });
        }
      },

      // Utility functions
      getVersion: (versionNumber: number) => {
        return get().versionMap.get(versionNumber);
      },

      getBranch: (branchId: string) => {
        return get().branchMap.get(branchId);
      },

      getDraft: (draftId: string) => {
        return get().draftMap.get(draftId);
      },

      getComparison: (comparisonId: string) => {
        return get().comparisonMap.get(comparisonId);
      },

      getVersionChanges: (versionNumber: number) => {
        const version = get().getVersion(versionNumber);
        return version?.changes || [];
      },

      getVersionsInRange: (startVersion: number, endVersion: number) => {
        const { versions } = get();
        return versions.filter(
          (v) => v.version >= startVersion && v.version <= endVersion,
        );
      },

      getVersionsByAuthor: (authorId: string) => {
        const { versions } = get();
        return versions.filter((v) => v.createdBy === authorId);
      },

      getVersionsByTag: (tag: string) => {
        const { versions } = get();
        return versions.filter((v) => v.tags.includes(tag));
      },

      getVersionsByDateRange: (startDate: Date, endDate: Date) => {
        const { versions } = get();
        return versions.filter(
          (v) => v.createdAt >= startDate && v.createdAt <= endDate,
        );
      },

      // Snapshot utilities
      generateSnapshot: async (projectId: string) => {
        try {
          const response = await fetch(
            `${config.api.baseUrl}/projects/${projectId}/snapshot`,
            {
              headers: getVersioningRequestHeaders(),
            },
          );

          if (!response.ok) {
            throw new Error(
              `Failed to generate snapshot: ${response.statusText}`,
            );
          }

          const result = await response.json();
          return result.data;
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to generate snapshot";
          });
          throw error;
        }
      },

      validateSnapshot: async (snapshot: ProjectSnapshot) => {
        try {
          const response = await fetch(
            `${config.api.baseUrl}/projects/snapshot/validate`,
            {
              method: "POST",
              headers: getVersioningRequestHeaders(),
              body: JSON.stringify({ snapshot }),
            },
          );

          if (!response.ok) {
            throw new Error(
              `Failed to validate snapshot: ${response.statusText}`,
            );
          }

          const result = await response.json();
          return result.data.isValid;
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to validate snapshot";
          });
          return false;
        }
      },

      applySnapshot: async (snapshot: ProjectSnapshot) => {
        const { projectId } = get();
        if (!projectId) throw new Error("No project loaded");

        try {
          const response = await fetch(
            `${config.api.baseUrl}/projects/${projectId}/snapshot/apply`,
            {
              method: "POST",
              headers: getVersioningRequestHeaders(),
              body: JSON.stringify({ snapshot }),
            },
          );

          if (!response.ok) {
            throw new Error(`Failed to apply snapshot: ${response.statusText}`);
          }

          // Refresh version data after applying snapshot
          await get().refreshVersionData();
        } catch (error) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : "Failed to apply snapshot";
          });
          throw error;
        }
      },

      // UI and view management
      setVersionFilter: (filter) => {
        set((state) => {
          if (filter.showAllVersions !== undefined) {
            state.showAllVersions = filter.showAllVersions;
          }
          if (filter.showBranchVersionsOnly !== undefined) {
            state.showBranchVersionsOnly = filter.showBranchVersionsOnly;
          }
          if (filter.tag !== undefined) {
            state.versionFilterTag = filter.tag;
          }
        });
      },

      setSortOptions: (sortBy, sortOrder) => {
        set((state) => {
          state.sortBy = sortBy;
          state.sortOrder = sortOrder;
        });
      },

      clearSelection: () => {
        set((state) => {
          state.selectedVersion = null;
          state.currentComparison = null;
        });
      },

      // Error handling
      setError: (error: string | null) => {
        set((state) => {
          state.error = error;
        });
      },

      clearError: () => {
        set((state) => {
          state.error = null;
        });
      },

      // Performance and caching
      enableCaching: (enabled: boolean) => {
        set((state) => {
          state.enableVersionCaching = enabled;
          if (!enabled) {
            state.versionCache.clear();
          }
        });
      },

      clearCache: () => {
        set((state) => {
          state.versionCache.clear();
        });
      },

      preloadVersions: async (versionNumbers: number[]) => {
        const { projectId, enableVersionCaching, versionCache } = get();
        if (!projectId || !enableVersionCaching) return;

        const uncachedVersions = versionNumbers.filter(
          (v) => !versionCache.has(`version-${v}`),
        );

        if (uncachedVersions.length === 0) return;

        try {
          const response = await fetch(
            `${config.api.baseUrl}/projects/${projectId}/versions/batch`,
            {
              method: "POST",
              headers: getVersioningRequestHeaders(),
              body: JSON.stringify({ versions: uncachedVersions }),
            },
          );

          if (response.ok) {
            const result = await response.json();
            const versionData = result.data;

            set((state) => {
              versionData.forEach((version: ProjectVersion) => {
                state.versionCache.set(`version-${version.version}`, version);
              });
            });
          }
        } catch (error) {
          console.warn("Failed to preload versions:", error);
        }
      },

      // Change tracking methods
      trackComponentChange: (
        changeType,
        componentId,
        pageId,
        field?,
        oldValue?,
        newValue?,
        description?,
      ) => {
        // Get userId from auth store state (not using hooks inside store)
        const authStore = useAuthStore.getState();
        const userId = authStore.user?.id || "anonymous";

        const change: VersionChange = {
          id: `change_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          type: changeType,
          target: "component",
          targetId: componentId,
          pageId,
          field,
          oldValue,
          newValue,
          userId,
          timestamp: new Date(),
          description: description || `Component ${changeType}`,
        };

        get().addChangeToTracking(change);
      },

      trackPageChange: (
        changeType,
        pageId,
        field?,
        oldValue?,
        newValue?,
        description?,
      ) => {
        // Get userId from auth store state (not using hooks inside store)
        const authStore = useAuthStore.getState();
        const userId = authStore.user?.id || "anonymous";

        const change: VersionChange = {
          id: `change_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          type: changeType,
          target: "page",
          targetId: pageId,
          field,
          oldValue,
          newValue,
          userId,
          timestamp: new Date(),
          description: description || `Page ${changeType}`,
        };

        get().addChangeToTracking(change);
      },

      trackChange: (change: VersionChange) => {
        get().addChangeToTracking(change);
      },

      addChangeToTracking: (change: VersionChange) => {
        set((state) => {
          state.pendingChanges.push(change);
          // Keep changeBuffer in sync for test compatibility
          state.changeBuffer = [...state.pendingChanges];
        });
      },

      clearChanges: () => {
        set((state) => {
          state.pendingChanges = [];
          state.changeBuffer = [];
        });
      },

      getPendingChanges: () => {
        return get().pendingChanges;
      },

      createSnapshot: (pages, pageComponents) => {
        // Convert Map to PageSnapshot format
        const pageSnapshots: PageSnapshot[] = pages.map((page) => {
          const components = pageComponents.get(page.id) || [];
          return {
            pageId: page.id,
            pageName: page.name,
            components,
            canvasSettings: page.canvasSettings,
            navigation: page.navigation,
            metadata: page.metadata,
          };
        });

        // Create a proper ProjectSnapshot matching the interface
        return {
          pages: pageSnapshots,
          navigation: {
            navigationStyle: NavigationStyle.TABS,
            showPageTabs: true,
            showBreadcrumbs: false,
            enableSearch: false,
            compactMode: false,
            sidebarPosition: "left" as const,
            tabsPosition: "top" as const,
            customNavigationOrder: [],
            navigationGroups: [],
          },
          globalSettings: {
            autoSave: true,
            autoSaveInterval: 30000,
            maxVersions: 50,
            allowComments: true,
            allowRealTimeEditing: true,
            requireApproval: false,
            allowPageCreation: true,
            maxPages: 50,
            defaultPageTemplate: PageTemplate.BLANK,
            enableCrossPageLinks: false,
            sharedComponentLibrary: false,
          },
          metadata: {
            totalComponents: pageSnapshots.reduce((sum, page) => sum + page.components.length, 0),
            totalPages: pageSnapshots.length,
            complexity: pageSnapshots.length > 5 ? "complex" : pageSnapshots.length > 2 ? "moderate" : "simple",
            estimatedLoadTime: 0,
            optimizationHints: [],
          },
        } as ProjectSnapshot;
      },

      // Enhanced version comparison with conflict detection
      compareVersionsWithConflictDetection: async (
        baseSnapshot,
        sourceSnapshot,
        targetSnapshot,
      ) => {
        return benchmarkVersioningOperation("compareVersions", async () => {
          // Create context for conflict detection
          const context: ConflictContext = {
            baseSnapshot,
            sourceSnapshot,
            targetSnapshot,
            sourceChanges: [], // Would be extracted from snapshots in real implementation
            targetChanges: [],
          };

          // Run conflict detection
          const conflictResult = await detectVersionConflicts(context);

          // Get userId from auth store state (not using hooks inside store)
          const authStore = useAuthStore.getState();
          const userId = authStore.user?.id || "anonymous";

          // Create comparison result
          const comparison: VersionComparison = {
            id: generateId(),
            fromVersion: 1, // Default version since ProjectSnapshot doesn't have version
            toVersion: 2, // Default version since ProjectSnapshot doesn't have version
            changes: [], // Would be calculated from snapshots
            conflictsDetected: conflictResult.hasConflicts,
            conflicts: conflictResult.conflicts,
            generatedAt: new Date(),
            generatedBy: userId,
          };

          // Store comparison in state
          set((state) => {
            state.comparisons.push(comparison);
            state.comparisonMap.set(comparison.id, comparison);
            state.currentComparison = comparison.id;

            if (comparison.conflictsDetected) {
              state.activeConflicts.push(...comparison.conflicts);
              state.metadata.hasUnresolvedConflicts = true;
            }

            state.lastUpdateTimestamp = Date.now();
          });

          return comparison;
        });
      },
    })),
  ),
);

// Selectors for optimized component rendering and performance
export const useVersioningProject = () =>
  useVersioningStore((state) => state.projectId);
export const useVersioningVersions = () =>
  useVersioningStore((state) => state.versions);
export const useVersioningCurrentVersion = () =>
  useVersioningStore((state) => state.currentVersion);
export const useVersioningSelectedVersion = () =>
  useVersioningStore((state) => state.selectedVersion);

export const useVersioningBranches = () =>
  useVersioningStore((state) => state.branches);
export const useVersioningCurrentBranch = () =>
  useVersioningStore((state) => state.currentBranch);

export const useVersioningDrafts = () =>
  useVersioningStore((state) => state.drafts);
export const useVersioningCurrentDraft = () =>
  useVersioningStore((state) => state.currentDraft);

export const useVersioningComparisons = () =>
  useVersioningStore((state) => state.comparisons);
export const useVersioningActiveConflicts = () =>
  useVersioningStore((state) => state.activeConflicts);

export const useVersioningLoading = () =>
  useVersioningStore((state) => state.isLoading);
export const useVersioningError = () =>
  useVersioningStore((state) => state.error);
export const useVersioningMetadata = () =>
  useVersioningStore((state) => state.metadata);

// Advanced selectors with memoization
export const useFilteredVersions = () => {
  const versions = useVersioningVersions();
  const showAllVersions = useVersioningStore((state) => state.showAllVersions);
  const showBranchVersionsOnly = useVersioningStore(
    (state) => state.showBranchVersionsOnly,
  );
  const versionFilterTag = useVersioningStore(
    (state) => state.versionFilterTag,
  );
  const currentBranch = useVersioningCurrentBranch();
  const sortBy = useVersioningStore((state) => state.sortBy);
  const sortOrder = useVersioningStore((state) => state.sortOrder);

  return useMemo(() => {
    let filtered = [...versions];

    // Apply filters
    if (!showAllVersions && currentBranch && showBranchVersionsOnly) {
      // Filter by current branch - would need branch-version relationship data
      // For now, show all versions
    }

    if (versionFilterTag) {
      filtered = filtered.filter((v) => v.tags.includes(versionFilterTag));
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case "date":
          comparison = (new Date(a.createdAt)).getTime() - (new Date(b.createdAt)).getTime();
          break;
        case "version":
          comparison = a.version - b.version;
          break;
        case "author":
          comparison = a.createdBy.localeCompare(b.createdBy);
          break;
        default:
          comparison = 0;
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });

    return filtered;
  }, [
    versions,
    showAllVersions,
    showBranchVersionsOnly,
    versionFilterTag,
    currentBranch,
    sortBy,
    sortOrder,
  ]);
};

export const useSortedBranches = () => {
  const branches = useVersioningBranches();

  return useMemo(() => {
    return [...branches].sort((a, b) => {
      // Sort by status (active first), then by name
      if (a.status !== b.status) {
        if (a.status === "active") return -1;
        if (b.status === "active") return 1;
        return a.status.localeCompare(b.status);
      }
      return a.name.localeCompare(b.name);
    });
  }, [branches]);
};

export const usePendingDrafts = () => {
  const drafts = useVersioningDrafts();

  return useMemo(() => {
    return drafts.filter((d) => d.status === "pending_review");
  }, [drafts]);
};

export const useUnresolvedConflicts = () => {
  const activeConflicts = useVersioningActiveConflicts();

  return useMemo(() => {
    return activeConflicts.filter((c) => !c.resolvedAt);
  }, [activeConflicts]);
};

// Persistence to localStorage
const VERSIONING_STORAGE_KEY = "terramatrix-versioning-preferences";

// Subscribe to changes for localStorage persistence
useVersioningStore.subscribe(
  (state) => ({
    showAllVersions: state.showAllVersions,
    showBranchVersionsOnly: state.showBranchVersionsOnly,
    sortBy: state.sortBy,
    sortOrder: state.sortOrder,
    enableVersionCaching: state.enableVersionCaching,
  }),
  (preferences) => {
    try {
      localStorage.setItem(VERSIONING_STORAGE_KEY, JSON.stringify(preferences));
    } catch (error) {
      console.warn("Failed to save versioning preferences:", error);
    }
  },
);

// Load preferences from localStorage on initialization
try {
  const savedPreferences = localStorage.getItem(VERSIONING_STORAGE_KEY);
  if (savedPreferences) {
    const raw: unknown = JSON.parse(savedPreferences);
    const result = VersioningPreferencesSchema.safeParse(raw);
    if (result.success) {
      const preferences = result.data;
      useVersioningStore.setState((state) => ({
        ...state,
        showAllVersions: preferences.showAllVersions ?? state.showAllVersions,
        showBranchVersionsOnly:
          preferences.showBranchVersionsOnly ?? state.showBranchVersionsOnly,
        sortBy: preferences.sortBy ?? state.sortBy,
        sortOrder: preferences.sortOrder ?? state.sortOrder,
        enableVersionCaching:
          preferences.enableVersionCaching ?? state.enableVersionCaching,
      }));
    }
  }
} catch (error) {
  console.warn("Failed to load versioning preferences:", error);
}

// Integration helper for editor store
export const useVersioningIntegration = () => {
  const store = useVersioningStore();

  return {
    // Core operations that editor store can use
    createVersionFromCurrentState: async (
      name?: string,
      description?: string,
      tags?: string[],
    ) => {
      // This would be called by editor store when creating a version
      const snapshot = await store.generateSnapshot(store.projectId!);
      return store.createVersion({
        name,
        description,
        snapshot,
        changes: [], // Changes would be calculated by editor store
        tags,
      });
    },

    // Restore a version to editor store
    restoreVersionToEditor: async (versionNumber: number) => {
      const version = store.getVersion(versionNumber);
      if (version && store.projectId) {
        await store.restoreVersion(versionNumber);
        return version.snapshot;
      }
      return null;
    },

    // Get current state for version creation
    getCurrentSnapshot: () => store.generateSnapshot(store.projectId!),

    // Initialize versioning when project loads
    initializeForProject: (projectId: string) =>
      store.initializeVersioning(projectId),

    // Clean up when project unloads
    cleanup: () => {
      useVersioningStore.setState({
        projectId: null,
        versions: [],
        versionMap: new Map(),
        currentVersion: null,
        selectedVersion: null,
        branches: [],
        branchMap: new Map(),
        currentBranch: null,
        drafts: [],
        draftMap: new Map(),
        currentDraft: null,
        comparisons: [],
        comparisonMap: new Map(),
        currentComparison: null,
        activeConflicts: [],
        resolvedConflicts: [],
        error: null,
      });
    },
  };
};

export default useVersioningStore;
